<script setup>
import newAppVersionAlert from '@shared-components/ui/alerts/newAppVersionAlert.vue'
import {defineAsyncComponent, onMounted, computed} from 'vue'
import {useRoute} from 'vue-router'
import {storeToRefs} from 'pinia'
import {useAuthenticationStore, useDefaultStore} from '@/stores'
import {cleanupLocalStorage, getUTMParams, isProduction, removeUTMParams} from '@shared/utils/helpers'
import {useCompanyStore} from '@/views/companies/CompanyStore'
import SidebarMenu from '@/components/side-bar/SidebarMenu.vue'
import TheCard from '@shared-components/TheCard.vue'
import ClickupAuthModal from '@/components/clickup/ClickupAuthModal.vue'
import {useClickupStore} from '@/views/clickup/ClickupStore'
import {loadHotjar} from '@shared/integrations/hotjar/hotjar'
import TheLayoutHeader from '@/components/ui/headers/TheLayoutHeader.vue'
import BalanceModalAlert from '@shared-components/ui/alerts/BalanceModalAlert.vue'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import ImpersonationPanel from '@/components/impersonation/ImpersonationPanel.vue'
import ManageFundsModal from '@/views/ad-account-requests/top-up/components/manage-funds-modal/ManageFundsModal.vue'
import {setupImpersonationHrefObserver} from '@shared-utils/auth/impersonation/impersonationObserver'

dayjs.extend(isSameOrBefore)

const CompanyModal = defineAsyncComponent(() => import('@/views/companies/components/CompanyModal.vue'))
const InvoiceModal = defineAsyncComponent(() => import('@/views/finance/components/InvoiceModal.vue'))

const route = useRoute()
const {
  pingBackend,
  sendAnalytics,
  openBalanceAlertModal,
  closeBalanceAlertModalAndFetch,
  closeManageFundsModal,
  closeInvoiceModal,
  closeBalanceAlertModal,
} = useDefaultStore()
const {toggleCompanyChangeTrigger} = useCompanyStore()

const {loadAuthUserInfo, fetchPerformanceReportAvailability} = useAuthenticationStore()

// Refs
const {hasNewVersion, showManageFundsModal, showInvoiceModal, showBalanceAlertModal} = storeToRefs(useDefaultStore())
const {showClickupAuthModal} = storeToRefs(useClickupStore())

const {showCompanyModal, companyToEditHash, companyTab} = storeToRefs(useCompanyStore())
const {user, isCoreTeam, userHasCompany, isAgencyClient, canUseInvoices, isImpersonated} =
  storeToRefs(useAuthenticationStore())
const utmFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

// Computed
const withCardWrap = computed(() => {
  return route.name !== 'ad-account-requests.top-up.list' && (route.name !== 'home' || isCoreTeam.value)
})
const balanceAlertModalCondition = computed(
  () =>
    user.value?.show_balance_alert &&
    user.value?.company?.balance_info?.low_balance_alert &&
    user.value?.company?.payment_terms === 'prepay',
)
const shouldOpenBalanceAlertModal = computed(() => !showBalanceAlertModal.value && balanceAlertModalCondition.value)

const isContentFullHeight = computed(
  () => route.name !== 'subscriptions.list' && route.name !== 'home' && route.meta.type !== 'form',
)
const contentColumnClass = computed(() => {
  const fullHeight = isContentFullHeight.value
  const notForm = route.meta.type !== 'form'

  return [
    'col px-0 sticky-col',
    {
      'd-flex-full-height': fullHeight,
      'vh-100': fullHeight,
      'overflow-x-hidden': fullHeight || notForm,
    },
  ]
})

// Methods
onMounted(async () => {
  runPingBackend
  reloadUserInfoAndCheckBalance
  handleToggleBalanceAlertModal()
  cleanupLocalStorage()
  pingBackend()

  if (userHasCompany.value) {
    fetchPerformanceReportAvailability()
  }

  if (isImpersonated.value) {
    setupImpersonationHrefObserver()
  }

  if (isProduction() && userHasCompany.value) {
    loadHotjar(5283256, 6)
  }

  await handleUTMParamsAnalytics()
})

const runPingBackend = setInterval(
  () => {
    pingBackend()
  },
  isProduction ? 20 * 1000 : 120 * 1000,
)

const reloadUserInfoAndCheckBalance = setInterval(
  async () => {
    await handleClientBalanceCheck()
  },
  isProduction() ? 3600 * 1000 : 120 * 1000,
)

const handleClientBalanceCheck = async () => {
  if (isAgencyClient.value && canUseInvoices.value) {
    await loadAuthUserInfo()
  }

  handleToggleBalanceAlertModal()
}

const handleToggleBalanceAlertModal = () => {
  if (shouldOpenBalanceAlertModal.value) {
    return openBalanceAlertModal()
  }

  if (!balanceAlertModalCondition.value) {
    closeBalanceAlertModal()
  }
}

const handleUTMParamsAnalytics = async () => {
  const utmParams = getUTMParams(utmFields)

  if (utmParams.utm_source) {
    const payload = {
      event: 'PageView',
      ...utmParams,
    }

    try {
      await handleSendAnalytics(payload)
      removeUTMParams(utmFields)
    } catch (error) {
      console.error(error)
    }
  }
}

const handleSendAnalytics = async params => {
  try {
    await sendAnalytics(params)
  } catch (error) {
    console.error(error)
    throw error
  }
}
</script>

<template>
  <teleport to="body">
    <div>
      <CompanyModal
        v-if="showCompanyModal && isCoreTeam"
        :company-hash="companyToEditHash"
        :tab="companyTab"
        @hidden="showCompanyModal = false"
        @updated="toggleCompanyChangeTrigger"
        @created="toggleCompanyChangeTrigger"
        id="layout-company-modal"
      />

      <manage-funds-modal v-if="showManageFundsModal" @hidden="closeManageFundsModal" id="layout-manage-funds-modal" />

      <invoice-modal
        v-if="showInvoiceModal"
        @hidden="closeInvoiceModal"
        id="layout-invoice-modal"
        @created="handleClientBalanceCheck"
      />

      <transition name="slide-up">
        <balance-modal-alert v-if="showBalanceAlertModal" @hidden="closeBalanceAlertModalAndFetch" />
      </transition>
    </div>
  </teleport>
  <ClickupAuthModal v-if="showClickupAuthModal" @hidden="showClickupAuthModal = false" />

  <div class="main-layout vh-100">
    <div class="container-fluid">
      <div class="row flex-nowrap main-bg">
        <div class="position-relative col-auto px-0">
          <sidebar-menu :full-height="!isImpersonated" />
        </div>
        <div :class="contentColumnClass">
          <impersonation-panel v-if="isImpersonated" />
          <div class="d-flex flex-column flex-1-1-auto py-4 px-3">
            <the-layout-header class="mb-4" />

            <template v-if="withCardWrap">
              <the-card
                :class="[
                  'border-radius-lg',
                  isContentFullHeight ? 'd-flex flex-column flex-1-1-auto full-width card-max-height-vh' : '',
                ]"
              >
                <router-view />
              </the-card>
            </template>
            <template v-else>
              <router-view />
            </template>
          </div>
        </div>
      </div>
    </div>

    <transition name="fade">
      <newAppVersionAlert v-if="hasNewVersion" />
    </transition>
  </div>
</template>

<style lang="scss">
.main-bg {
  background: url('~@shared-images/grunge/bg.webp') center no-repeat;
  background-size: 100% 100%;
}

//.card-max-height-vh {
//  height: calc(100vh - 200px);
//}

//.sticky-col {
//  position: sticky;
//  top: 0;
//  z-index: 10;
//}
</style>
